import { useQuery } from '@tanstack/react-query';
import { Card, Center, Loader, Alert, Text, Stack, Group, Table, Box } from '@mantine/core';
import { Timer } from 'lucide-react';

import { gamesApi, gameExtrasApi } from '../../api';
import { Leaderboard } from '../Leaderboard';
import { Eyebrow } from '../Eyebrow';
import { GroupTag } from '../GroupTag';
import { numStyle } from '../../theme';
import { formatDuration, formatPoints, plural } from '../../utils/format';

function Wrap({ children }: { children: React.ReactNode }) {
  return <Card padding={0}>{children}</Card>;
}

function Empty({ text }: { text: string }) {
  return <Card><Text c="dimmed" ta="center" py="lg">{text}</Text></Card>;
}

function Loading() {
  return <Card><Center h={120}><Loader size="sm" /></Center></Card>;
}

function Failed({ error }: { error: unknown }) {
  return (
    <Alert color="red" title="Výsledky se nepodařilo načíst">
      {(error as any)?.response?.data?.error || (error as any)?.message}
    </Alert>
  );
}

export function HostinaResults({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['hostina', gameUuid],
    queryFn: () => gamesApi.getHostinaLeaderboard(gameUuid),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Failed error={q.error} />;
  if (!q.data?.length) return <Empty text="Zatím nikdo nic nenaskenoval." />;
  return (
    <Wrap>
      <Leaderboard
        rows={q.data.map((h) => ({
          key: h.groupId,
          name: h.groupName,
          sub: plural(h.scannedCount, 'sken', 'skeny', 'skenů'),
          value: h.totalPoints,
          color: h.groupColor,
        }))}
      />
    </Wrap>
  );
}

export function PocitadloResults({ gameUuid }: { gameUuid: string }) {
  const board = useQuery({
    queryKey: ['pocitadlo', gameUuid],
    queryFn: () => gamesApi.getPocitadloLeaderboard(gameUuid),
  });
  const history = useQuery({
    queryKey: ['pocitadloHistory', gameUuid],
    queryFn: () => gameExtrasApi.getPocitadloHistory(gameUuid),
  });

  if (board.isLoading) return <Loading />;
  if (board.error) return <Failed error={board.error} />;
  if (!board.data?.length) return <Empty text="Zatím nejsou zapsané žádné body." />;

  return (
    <Stack gap="sm">
      <Wrap>
        <Leaderboard
          rows={board.data.map((p) => ({
            key: p.group_id,
            name: p.group_name,
            sub: plural(p.transaction_count, 'transakce', 'transakce', 'transakcí'),
            value: p.total_points,
            color: p.group_color,
          }))}
        />
      </Wrap>

      {!!history.data?.length && (
        <Card padding={0}>
          <Group p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Text fw={700} fz="sm">Historie zápisů</Text>
          </Group>
          <Table.ScrollContainer minWidth={420}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={140}>Čas</Table.Th>
                  <Table.Th>Skupina</Table.Th>
                  <Table.Th w={90} ta="right">Body</Table.Th>
                  <Table.Th w={120}>Zapsal</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {history.data.map((t) => (
                  <Table.Tr key={t.id}>
                    <Table.Td>
                      <Text fz="sm" c="dimmed" style={numStyle}>
                        {new Date(t.recorded_at).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })}
                      </Text>
                    </Table.Td>
                    <Table.Td><GroupTag name={t.group_name} color={t.group_color} /></Table.Td>
                    <Table.Td ta="right">
                      <Text fz="sm" fw={600} style={numStyle} c={t.points < 0 ? 'red' : 'forest'}>
                        {t.points > 0 ? '+' : ''}{formatPoints(t.points)}
                      </Text>
                    </Table.Td>
                    <Table.Td><Text fz="sm" c="dimmed">{t.recorded_by || '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </Stack>
  );
}

export function StopkyResults({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['stopky', gameUuid],
    queryFn: () => gamesApi.getStopkyTimes(gameUuid),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Failed error={q.error} />;
  if (!q.data?.length) return <Empty text="Zatím nikdo neměřil čas." />;

  return (
    <Stack gap="sm">
      {q.data.map((device) => {
        const rows = [...device.groups].sort((a, b) => a.elapsedMilliseconds - b.elapsedMilliseconds);
        const best = rows[0]?.elapsedMilliseconds ?? 0;
        return (
          <Card key={device.deviceClientId} padding={0}>
            <Group p="sm" gap={8} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
              <Timer size={15} color="var(--mantine-color-dimmed)" />
              <Text fw={700} fz="sm" style={{ flex: 1 }}>{device.deviceName || 'Bez názvu'}</Text>
              <Eyebrow>měřicí místo · nejlepší {formatDuration(best)}</Eyebrow>
            </Group>
            <Leaderboard
              showBars={false}
              rows={rows.map((g, i) => ({
                key: g.groupId,
                name: g.groupName,
                // Kratší čas je lepší, takže pruh podle hodnoty by lhal — proto jen odstup.
                sub: i === 0 ? 'nejrychlejší' : `+${formatDuration(g.elapsedMilliseconds - best)}`,
                value: g.elapsedMilliseconds,
                display: formatDuration(g.elapsedMilliseconds),
                color: g.groupColor,
              }))}
            />
          </Card>
        );
      })}
    </Stack>
  );
}

export function DominationsResults({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['dominations', gameUuid],
    queryFn: () => gamesApi.getDominationsScore(gameUuid),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Failed error={q.error} />;
  const data = q.data;
  if (!data || (!data.territories.length && !data.totalBlackScore && !data.totalWhiteScore)) {
    return <Empty text="Zatím se nebojovalo o žádné teritorium." />;
  }

  const total = data.totalBlackScore + data.totalWhiteScore || 1;
  const label: Record<string, string> = { black: 'Černí', white: 'Bílí', neutral: 'Neutrální' };

  return (
    <Stack gap="sm">
      <Card>
        <Stack gap="sm">
          <Box style={{ display: 'flex', height: 30, borderRadius: 5, overflow: 'hidden', border: '1px solid var(--mantine-color-default-border)' }}>
            <Box style={{ flex: data.totalBlackScore / total, background: '#1B1B1B', color: '#fff', display: 'grid', placeItems: 'center', ...numStyle, fontWeight: 700 }}>
              {data.totalBlackScore}
            </Box>
            <Box style={{ flex: data.totalWhiteScore / total, background: '#F5F5F2', color: '#1A1A1A', display: 'grid', placeItems: 'center', ...numStyle, fontWeight: 700 }}>
              {data.totalWhiteScore}
            </Box>
          </Box>
          <Group justify="space-between">
            <Eyebrow>Černí · {data.territories.filter((t) => t.currentState === 'black').length} teritorií</Eyebrow>
            <Eyebrow>Bílí · {data.territories.filter((t) => t.currentState === 'white').length} teritorií</Eyebrow>
          </Group>
        </Stack>
      </Card>

      <Card padding={0}>
        <Group p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Text fw={700} fz="sm" style={{ flex: 1 }}>Teritoria</Text>
          <Eyebrow>jedno zařízení = jedno teritorium</Eyebrow>
        </Group>
        <Table.ScrollContainer minWidth={380}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Zařízení</Table.Th>
                <Table.Th w={110}>Drží</Table.Th>
                <Table.Th w={80} ta="right">Černí</Table.Th>
                <Table.Th w={80} ta="right">Bílí</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.territories.map((t) => (
                <Table.Tr key={t.deviceId}>
                  <Table.Td fw={600} fz="sm">{t.deviceId}</Table.Td>
                  <Table.Td><Text fz="sm">{label[t.currentState] ?? t.currentState}</Text></Table.Td>
                  <Table.Td ta="right"><Text fz="sm" style={numStyle}>{t.blackScore}</Text></Table.Td>
                  <Table.Td ta="right"><Text fz="sm" style={numStyle}>{t.whiteScore}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}

export function MrtvolisteResults({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['scores', gameUuid],
    queryFn: () => gamesApi.getScores(gameUuid),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Failed error={q.error} />;
  if (!q.data?.length) return <Empty text="Zatím nikdo nezemřel." />;

  return (
    <Card padding={0}>
      <Table.ScrollContainer minWidth={440}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={60}>Číslo</Table.Th>
              <Table.Th>Člen</Table.Th>
              <Table.Th>Skupina</Table.Th>
              <Table.Th w={80} ta="right">Smrtí</Table.Th>
              <Table.Th w={120} ta="right">Mrtvý celkem</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {q.data.map((s) => (
              <Table.Tr key={s.member_number}>
                <Table.Td><Text style={numStyle} fz="sm" fw={600}>{s.member_number}</Text></Table.Td>
                <Table.Td fw={600} fz="sm">{s.name ?? 'Bez jména'}</Table.Td>
                <Table.Td><GroupTag name={s.group_name} color={s.group_color} /></Table.Td>
                <Table.Td ta="right"><Text style={numStyle} fz="sm">{s.death_count}×</Text></Table.Td>
                <Table.Td ta="right"><Text style={numStyle} fz="sm">{s.total_minutes} min</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}
